import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

export function StatCard({ label, value, sub, icon: Icon, tone = "default", className, onClick }) {
  const tones = { default: "text-ink", good: "text-stock-in", warn: "text-stock-low", bad: "text-stock-out", brand: "text-brand" };
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={cn("panel panel-pad animate-rise text-left w-full", onClick && "hover:border-brand-ring transition-colors", className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="eyebrow">{label}</div>
          <div className={cn("stat-figure mt-2", tones[tone])}>{value}</div>
          {sub && <div className="mt-1.5 text-[12.5px] text-muted2">{sub}</div>}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-brand-light flex items-center justify-center shrink-0">
            <Icon className="w-[18px] h-[18px] text-brand" />
          </div>
        )}
      </div>
    </Tag>
  );
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && (
        <div className="w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mb-4">
          <Icon className="w-5 h-5 text-brand" />
        </div>
      )}
      <h3 className="font-display text-lg font-semibold text-ink">{title}</h3>
      {body && <p className="mt-1.5 text-sm text-muted2 max-w-sm">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("w-5 h-5 animate-spin text-brand", className)} />;
}

export function LoadingPanel({ rows = 5 }) {
  return (
    <div className="panel panel-pad space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 rounded-lg bg-line/50 animate-pulse" style={{ animationDelay: `${i * 60}ms` }} />
      ))}
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface">
      <Spinner className="w-6 h-6" />
    </div>
  );
}

function useGate() {
  const { user, loading } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();
  if (loading || user === null || (user && tenantLoading)) return { wait: true };
  if (!user) return { to: "/login" };
  if (user.must_change_password) return { to: "/set-password" };
  return { user, tenantId };
}

/** Signed in and inside a workspace. */
export function ProtectedRoute() {
  const g = useGate();
  const { pathname } = useLocation();
  if (g.wait) return <FullPageSpinner />;
  if (g.to) return <Navigate to={g.to} replace />;
  if (!g.tenantId) return <Navigate to={g.user.role === "owner" ? "/platform" : "/login"} replace />;
  // A writer login has its own small app; anything else sends them to their dashboard.
  if (g.user.role === "writer" && !WRITER_PAGES.includes(pathname.replace(/\/$/, ""))) return <Navigate to="/dashboard" replace />;
  if (g.user.role !== "writer" && ["/dashboard", "/my-songs"].includes(pathname)) return <Navigate to="/overview" replace />;
  return <Outlet />;
}

const WRITER_PAGES = ["/dashboard", "/my-songs", "/royalties", "/payouts", "/support", "/profile"];

/** Anyone who can change the catalog — not writer logins. */
export function EditorRoute() {
  const g = useGate();
  if (g.wait) return <FullPageSpinner />;
  if (g.to) return <Navigate to={g.to} replace />;
  if (!g.tenantId) return <Navigate to={g.user.role === "owner" ? "/platform" : "/login"} replace />;
  if (g.user.role === "writer") return <Navigate to="/catalog" replace />;
  return <Outlet />;
}

/** Settings and Team. */
export function AdminRoute() {
  const g = useGate();
  if (g.wait) return <FullPageSpinner />;
  if (g.to) return <Navigate to={g.to} replace />;
  if (!g.tenantId) return <Navigate to={g.user.role === "owner" ? "/platform" : "/login"} replace />;
  if (!["owner", "admin"].includes(g.user.role)) return <Navigate to="/overview" replace />;
  return <Outlet />;
}

export function OwnerRoute() {
  const g = useGate();
  if (g.wait) return <FullPageSpinner />;
  if (g.to) return <Navigate to={g.to} replace />;
  if (g.user.role !== "owner") return <Navigate to="/overview" replace />;
  return <Outlet />;
}

export function SignedInRoute() {
  const g = useGate();
  if (g.wait) return <FullPageSpinner />;
  if (g.to && g.to !== "/set-password") return <Navigate to={g.to} replace />;
  return <Outlet />;
}
